import base64 from "base-64";

export const EVENT_PLATFORMS = [
  { id: "danzflo", label: "DanzFlo", icon: "bi-house", href: process.env.REACT_APP_MERCH_URL },
  { id: "merch", label: "Merch", icon: "bi-bag", platform: "merch", href: process.env.REACT_APP_MERCH_URL_HOME },
  { id: "social", label: "Social", icon: "bi-people", platform: "social", href: process.env.REACT_APP_SOCIAL_API_URL },
  { id: "event", label: "Event", icon: "bi-calendar2-event", to: "/" },
];

export const EVENT_EXPLORE = [
  { id: "merch", label: "Merch", icon: "bi-bag", platform: "merch", href: process.env.REACT_APP_MERCH_URL_HOME },
  { id: "social", label: "Social", icon: "bi-people", platform: "social", href: process.env.REACT_APP_SOCIAL_API_URL },
  { id: "classes", label: "Classes", icon: "bi-mortarboard", platform: "class", href: process.env.REACT_APP_CLASSES_URL },
  { id: "tv", label: "TV", icon: "bi-camera-reels", platform: "tv", href: process.env.REACT_APP_TV_API_URL },
  { id: "music", label: "Music", icon: "bi-music-note", platform: "music", href: process.env.REACT_APP_MUSIC_API_URL },
];

const PATH_ACTIVE_MAP = [
  { match: (p) => p === "/notifications", id: "notifications" },
  { match: (p) => p === "/search" || p.startsWith("/search?"), id: "search" },
  { match: (p) => p === "/", id: "event-list" },
  { match: (p) => p === "/wishlist", id: "wishlist" },
  { match: (p) => p === "/schedule", id: "calendar" },
  { match: (p) => p.startsWith("/myEvents/create"), id: "create-event" },
  { match: (p) => p === "/organizer/assign/guard", id: "assign-guard" },
  { match: (p) => p === "/organizer/MyEvents", id: "my-created-events" },
  { match: (p) => p === "/MyEvents", id: "my-events" },
  { match: (p) => p === "/promoter/events", id: "my-promoting-events" },
  { match: (p) => p === "/promocodes", id: "promo-code" },
  { match: (p) => p === "/myOrders" || p.startsWith("/myOrder/"), id: "orders" },
  { match: (p) => p === "/recivedOrders", id: "received-orders" },
  { match: (p) => p === "/organizer/attendees" || p.startsWith("/organizer/attendee/"), id: "attendees" },
  { match: (p) => p.startsWith("/organizer/myEarning"), id: "my-earnings" },
  { match: (p) => p.startsWith("/promoter/earning"), id: "my-promotion-earnings" },
  { match: (p) => /\/[^/]+\/userEvents\/?$/.test(p), id: "user-events" },
  {
    match: (p) => /\/[^/]+\/userPromotingEvents\/?$/.test(p),
    id: "user-promoting-events",
  },
];

const HUB_ROUTES = new Set([
  "/",
  "/wishlist",
  "/schedule",
  "/MyEvents",
  "/notifications",
  "/search",
  "/myOrders",
  "/recivedOrders",
  "/promocodes",
  "/promoter/events",
  "/organizer/MyEvents",
  "/organizer/assign/guard",
  "/organizer/attendees",
  "/organizer/myEarning",
  "/promoter/earning",
]);

const isHubRoute = (pathname = "") => {
  const path = String(pathname || "");
  if (HUB_ROUTES.has(path)) return true;
  return (
    path.startsWith("/MyEvents/") ||
    path.startsWith("/myOrder/") ||
    path.startsWith("/myEvents/") ||
    path.startsWith("/organizer/") ||
    path.startsWith("/promoter/") ||
    path.startsWith("/search")
  );
};

export const isProfileRoute = (pathname = "") =>
  /\/[^/]+\/(userEvents|userPromotingEvents)(?:\/|$)/.test(
    String(pathname || "")
  );

export { isHubRoute };

/** Profile username from route params or contextual query. */
export function getViewedProfileUserName(pathname = "", search = "") {
  const path = String(pathname || "");
  const queryName = new URLSearchParams(search || "").get("userName");

  const profileMatch = path.match(
    /^\/([^/]+)\/(userEvents|userPromotingEvents)(?:\/|$)/
  );
  if (profileMatch?.[1]) return profileMatch[1];

  const eventDetailMatch = path.match(/^\/([^/]+)\/event-details\//);
  if (eventDetailMatch?.[1]) return eventDetailMatch[1];

  // Auth handoff from social: /auth?token=…&userName=… or /Auth/:token?userName=…
  if (/^\/auth(?:\/|$)/i.test(path) && queryName) return queryName;

  // Event list hub can carry profile context from social (like Classes).
  if (path === "/") {
    if (queryName) return queryName;
  }

  if (!isHubRoute(path)) {
    if (queryName) return queryName;
  }

  return null;
}

export function isViewingOtherProfile(userDetails, viewedUserName) {
  if (!viewedUserName) return false;

  const currentUser = String(
    userDetails?.userName || userDetails?.username || ""
  ).toLowerCase();
  // If we can't resolve the logged-in username yet, treat as own hub so
  // Event list / Account links stay available instead of profile-only menu.
  if (!currentUser) return false;
  return viewedUserName.toLowerCase() !== currentUser;
}

export const hubRouteTarget = (pathname = "/", profileUserName = null) => ({
  pathname,
  search: profileUserName
    ? `?userName=${encodeURIComponent(profileUserName)}`
    : "",
});

export const getEventHubUrl = (pathname = "/", search = "") => {
  const base = (process.env.REACT_APP_BASE_URL || "/event").replace(/\/$/, "");
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const normalizedSearch =
    search && !search.startsWith("?") ? `?${search}` : search;
  if (normalized === "/" && !normalizedSearch) return `${base}/`;
  return `${base}${normalized}${normalizedSearch}`;
};

export function getActiveMenuId(pathname) {
  const hit = PATH_ACTIVE_MAP.find(({ match }) => match(pathname));
  return hit?.id || "event-list";
}

export function getOrganizerRegisterUrl(userDetails) {
  const redirectUrl = base64.encode(
    base64.encode(process.env.REACT_APP_BASE_URL)
  );
  return `${process.env.REACT_APP_MERCH_URL}/register-vendor/event/${redirectUrl}/${userDetails?._id}`;
}

export function buildEventMenu(userDetails, userName, profileUser = null) {
  const isOrganizer = userDetails?.userType === "organizer";
  const isPromoter = userDetails?.isPromoter === true;
  const viewingOtherProfile = isViewingOtherProfile(userDetails, userName);
  const profileIsPromoter =
    profileUser?.isPromoter === true || profileUser?.userType === "promoter";

  const hubItems = [
    // {
    //   id: "home",
    //   label: "Home",
    //   icon: "bi-house",
    //   href: process.env.REACT_APP_MERCH_URL,
    // },
    {
      id: "event-list",
      label: "Event list",
      icon: "bi-list",
      to: viewingOtherProfile ? hubRouteTarget("/", userName) : hubRouteTarget("/"),
    },
    ...(viewingOtherProfile
      ? [
          {
            id: "user-events",
            label: "User Events",
            icon: "bi-calendar2-event",
            to: `/${userName}/userEvents`,
          },
          ...(profileIsPromoter
            ? [
                {
                  id: "user-promoting-events",
                  label: "User Promoting Events",
                  icon: "bi-megaphone",
                  to: `/${userName}/userPromotingEvents`,
                  state: { userProfile: profileUser },
                },
              ]
            : []),
        ]
      : [
          {
            id: "wishlist",
            label: "Wishlist",
            icon: "bi-heart",
            to: hubRouteTarget("/wishlist"),
          },
        ]),
    ...(!viewingOtherProfile && isOrganizer
      ? [
          {
            id: "create-event",
            label: "Create Event",
            icon: "bi-calendar-plus",
            to: hubRouteTarget("/myEvents/create/basic_info"),
          },
          {
            id: "assign-guard",
            label: "Assign Guard",
            icon: "bi-shield-check",
            to: hubRouteTarget("/organizer/assign/guard"),
          },
        ]
      : []),
  ];

  const accountItems = userDetails && !viewingOtherProfile
    ? [
        ...(isOrganizer
          ? [
              {
                id: "my-created-events",
                label: "My Created Events",
                icon: "bi-plus-square",
                to: hubRouteTarget("/organizer/MyEvents"),
              },
            ]
          : []),
        {
          id: "my-events",
          label: "My Events",
          icon: "bi-ticket-perforated",
          to: hubRouteTarget("/MyEvents"),
        },
        ...(isPromoter
          ? [
              {
                id: "my-promoting-events",
                label: "My Promoting Events",
                icon: "bi-megaphone",
                to: hubRouteTarget("/promoter/events"),
              },
            ]
          : []),
        ...(isOrganizer
          ? [
              {
                id: "promo-code",
                label: "Promo Code",
                icon: "bi-ticket",
                to: hubRouteTarget("/promocodes"),
              },
            ]
          : []),
        {
          id: "orders",
          label: "Orders",
          icon: "bi-basket",
          to: hubRouteTarget("/myOrders"),
        },
        ...(isOrganizer
          ? [
              {
                id: "received-orders",
                label: "Received Orders",
                icon: "bi-box-seam",
                to: hubRouteTarget("/recivedOrders"),
              },
              {
                id: "attendees",
                label: "Attendees",
                icon: "bi-people",
                to: hubRouteTarget("/organizer/attendees"),
              },
              {
                id: "my-earnings",
                label: "My Earnings",
                icon: "bi-cash-coin",
                to: hubRouteTarget("/organizer/myEarning"),
              },
            ]
          : []),
        ...(isPromoter
          ? [
              {
                id: "my-promotion-earnings",
                label: "My Promotion Earnings",
                icon: "bi-graph-up",
                to: hubRouteTarget("/promoter/earning"),
              },
            ]
          : []),
        {
          id: "update-organizer",
          label: isOrganizer ? "Update Organizer Details" : "Register as Organizer",
          icon: "bi-pencil-square",
          href: getOrganizerRegisterUrl(userDetails),
        },
      ]
    : [];

  const sections = [{ type: "section", label: "Event Hub", items: hubItems }];

  if (accountItems.length) {
    sections.push({ type: "section", label: "Account", items: accountItems });
  }

  return sections;
}
